import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import {
  ArrowLeft, Send, Image, Clock, Shield, Star, Check, X,
  AlertCircle, MessageSquare, Loader2, Upload, Trash2, Sword, Flag, Heart
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import BountyPoster from "../components/BountyPoster";
import KofiModal from "../components/KofiModal";
import UserProfileModal from "../components/UserProfileModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format mesos: input is in millions, display as "X,000,000 mesos"
const formatMesos = (value) => {
  if (!value && value !== 0) return "";
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  const full = num * 1000000;
  return `${full.toLocaleString()} mesos`;
};

// Boss image mapping for bounties
const BOSS_IMAGE_MAPPING = {
  "Mano": "https://legends.ml/static/images/lib/monster/2220000.png",
  "Stumpy": "https://legends.ml/static/images/lib/monster/3220000.png",
  "Deo": "https://legends.ml/static/images/lib/monster/3220001.png",
  "King Clang": "https://legends.ml/static/images/lib/monster/5220000.png",
  "Seruf": "https://legends.ml/static/images/lib/monster/4220001.png",
  "Faust": "https://legends.ml/static/images/lib/monster/5220002.png",
  "Timer": "https://legends.ml/static/images/lib/monster/5220003.png",
  "Mushmom": "https://legends.ml/static/images/lib/monster/6130101.png",
  "Dyle": "https://legends.ml/static/images/lib/monster/6220000.png",
  "Zombie Mushmom": "https://legends.ml/static/images/lib/monster/6300005.png",
  "Zeno": "https://legends.ml/static/images/lib/monster/6220001.png",
  "Nine-Tailed Fox": "https://legends.ml/static/images/lib/monster/7220001.png",
  "Tae Roon": "https://legends.ml/static/images/lib/monster/7220000.png",
  "King Sage Cat": "https://legends.ml/static/images/lib/monster/7220002.png",
  "Jr. Balrog": "https://legends.ml/static/images/lib/monster/8130100.png",
  "Eliza": "https://legends.ml/static/images/lib/monster/8220000.png",
  "Snack Bar": "https://legends.ml/static/images/lib/monster/9410015.png",
  "Chimera": "https://legends.ml/static/images/lib/monster/8220002.png",
  "Blue Mushmom": "https://legends.ml/static/images/lib/monster/9400205.png",
  "Snowman": "https://legends.ml/static/images/lib/monster/8220001.png",
  "Headless Horseman": "https://legends.ml/static/images/lib/monster/9400549.png",
  "Manon": "https://legends.ml/static/images/lib/monster/9500174.png",
  "Griffey": "https://legends.ml/static/images/lib/monster/8180001.png",
  "BigFoot": "https://legends.ml/static/images/lib/monster/9400575.png",
  "Black Crow": "https://legends.ml/static/images/lib/monster/9400014.png",
  "Leviathan": "https://legends.ml/static/images/lib/monster/8220003.png",
  "Kacchuu Musha": "https://legends.ml/static/images/lib/monster/9400405.png",
  "Dodo": "https://legends.ml/static/images/lib/monster/8220004.png",
  "Anego / Female Boss": "https://legends.ml/static/images/lib/monster/9400121.png",
  "Lilynouch": "https://legends.ml/static/images/lib/monster/8220005.png",
  "Lyka": "https://legends.ml/static/images/lib/monster/8220006.png"
};
const getBossImage = (n) => { const b = n?.split(" - ")[0]; return BOSS_IMAGE_MAPPING[b] || "https://legends.ml/static/images/lib/monster/6130101.png"; };

const BountyDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [bounty, setBounty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reporting, setReporting] = useState(false);
  const [showKofiModal, setShowKofiModal] = useState(false);
  const [profileUserId, setProfileUserId] = useState(null);
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastMsgTimestamp = useRef(null);
  const pollRef = useRef(null);

  const fetchBounty = useCallback(async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API_URL}/api/bounties/${id}`, { headers });
      setBounty(res.data);
    } catch {
      toast.error("Bounty not found");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [id, token, navigate]);

  const fetchMessages = useCallback(async (initial = false) => {
    try {
      const params = {};
      if (!initial && lastMsgTimestamp.current) {
        params.since = lastMsgTimestamp.current;
      }
      const res = await axios.get(`${API_URL}/api/chat/bounty/${id}/messages`, { params });
      const msgs = res.data.messages || [];
      if (msgs.length > 0) {
        lastMsgTimestamp.current = msgs[msgs.length - 1].created_at;
        if (initial) {
          setMessages(msgs);
        } else {
          // Deduplicate by ID
          setMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const newMsgs = msgs.filter(m => !existingIds.has(m.id));
            return newMsgs.length > 0 ? [...prev, ...newMsgs] : prev;
          });
        }
      }
    } catch {
      // Silent fail for polling
    }
  }, [id]);

  useEffect(() => {
    fetchBounty();
    fetchMessages(true);
    // Fast polling for chat - 2 seconds, also refresh bounty state every 5s
    pollRef.current = setInterval(() => {
      fetchMessages(false);
    }, 2000);
    const bountyPoll = setInterval(fetchBounty, 5000); // Poll bounty state for confirm updates
    return () => {
      clearInterval(pollRef.current);
      clearInterval(bountyPoll);
    };
  }, [fetchBounty, fetchMessages]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendingRef = useRef(false);
  
  const handleSendMessage = async (screenshotUrl = null) => {
    const text = newMessage.trim();
    if (!text && !screenshotUrl) return;
    if (sendingRef.current) return; // Prevent double-send
    sendingRef.current = true;
    setSendingMessage(true);
    const savedMsg = newMessage;
    setNewMessage(""); // Clear immediately
    try {
      await axios.post(`${API_URL}/api/chat/bounty/${id}/messages`, {
        message: text || (screenshotUrl ? "Screenshot" : ""),
        screenshot_url: screenshotUrl
      }, { headers: { Authorization: `Bearer ${token}` } });
      fetchMessages(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
      setNewMessage(savedMsg); // Restore on failure
    } finally {
      setSendingMessage(false);
      sendingRef.current = false;
    }
  };

  const handleUploadScreenshot = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only images allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Max 5MB");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API_URL}/api/upload/screenshot`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      await handleSendMessage(res.data.url);
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const res = await axios.put(`${API_URL}/api/bounties/${id}/confirm`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchBounty();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to confirm");
    } finally {
      setConfirming(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Are you sure you want to cancel/drop this bounty?")) return;
    setConfirming(true);
    try {
      await axios.put(`${API_URL}/api/bounties/${id}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Bounty cancelled");
      fetchBounty();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel");
    } finally {
      setConfirming(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Permanently delete this bounty? This cannot be undone.")) return;
    setConfirming(true);
    try {
      await axios.delete(`${API_URL}/api/bounties/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Bounty deleted");
      navigate("/hunters-chance");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    } finally {
      setConfirming(false);
    }
  };

  const handleAcceptBounty = async () => {
    if (!user) {
      toast.error("Please log in to accept this bounty");
      return;
    }
    setConfirming(true);
    try {
      await axios.put(`${API_URL}/api/bounties/${id}/assign`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Bounty accepted! Contact the buyer using the IGNs provided.");
      fetchBounty();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept bounty");
    } finally {
      setConfirming(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      ACTIVE: "bg-green-500/20 text-green-300 border-green-500/40",
      ASSIGNED: "bg-blue-500/20 text-blue-300 border-blue-500/40",
      SUCCEEDED: "bg-yellow-500/20 text-yellow-300 border-yellow-500/40",
      CANCELLED: "bg-red-500/20 text-red-300 border-red-500/40",
      INACTIVE: "bg-gray-500/20 text-gray-300 border-gray-500/40",
    };
    return colors[status] || colors.ACTIVE;
  };

  const handleReport = async () => {
    if (!reportReason.trim()) return;
    setReporting(true);
    try {
      await axios.post(`${API_URL}/api/hc/report`, {
        target_type: "bounty",
        target_id: id,
        reason: reportReason.trim()
      });
      toast.success("Report submitted. Admins will review it.");
      setShowReportModal(false);
      setReportReason("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit report");
    } finally {
      setReporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-red-400" />
      </div>
    );
  }

  if (!bounty) return null;

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="bounty-detail-page">
      {/* Header */}
      <div className="sticky top-0 z-20 border-b" style={{ background: "linear-gradient(180deg, #2a0a0a 0%, #1a0505 100%)", borderColor: "rgba(220,38,38,0.3)" }}>
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-red-300 hover:bg-red-500/10" data-testid="bounty-detail-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-red-200">BOUNTY: {bounty.boss_name}</h1>
            <div className="flex items-center gap-2 text-xs text-red-300/60">
              <span>Posted by <span className="cursor-pointer hover:text-red-200 hover:underline" onClick={() => setProfileUserId(bounty.client_id)}>{bounty.client_username}</span></span>
              <span>|</span>
              <Badge className={`text-xs ${getStatusColor(bounty.status)}`}>{bounty.status}</Badge>
            </div>
          </div>
          <div className="text-right">
            <p className="text-yellow-400 font-bold">{formatMesos(bounty.reward)}</p>
            <p className="text-xs text-red-300/50">Reward</p>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Bounty Info */}
        <div className="lg:col-span-1 space-y-4">
          {/* Bounty Poster Card */}
          <BountyPoster bounty={bounty} compact={false} parchmentBg="" bossImage={getBossImage(bounty.boss_name)} />
          {/* Details Card */}
          <div className="rounded-xl p-4 space-y-3" style={{ background: "linear-gradient(180deg, #1a0808 0%, #120404 100%)", border: "1px solid rgba(220,38,38,0.25)" }}>
            <h2 className="text-sm font-semibold text-red-300 uppercase tracking-wider flex items-center gap-2">
              <Shield className="w-4 h-4" /> Bounty Details
            </h2>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-red-400/60">Boss</span>
                <span className="text-red-100 font-medium">{bounty.boss_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-red-400/60">Reward</span>
                <span className="text-yellow-400 font-bold">{formatMesos(bounty.reward)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-red-400/60">Status</span>
                <Badge className={`text-xs ${getStatusColor(bounty.status)}`}>{bounty.status}</Badge>
              </div>
              {bounty.expires_at && (
                <div className="flex justify-between">
                  <span className="text-red-400/60">Expires</span>
                  <span className="text-red-200 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(bounty.expires_at).toLocaleString()}
                  </span>
                </div>
              )}
              {bounty.message && (
                <div className="pt-2 border-t border-red-900/30">
                  <p className="text-xs text-red-400/60 mb-1">Message</p>
                  <p className="text-red-200 text-sm italic">"{bounty.message}"</p>
                </div>
              )}
            </div>
          </div>

          {/* Participants */}
          <div className="rounded-xl p-4 space-y-3" style={{ background: "linear-gradient(180deg, #1a0808 0%, #120404 100%)", border: "1px solid rgba(220,38,38,0.25)" }}>
            <h2 className="text-sm font-semibold text-red-300 uppercase tracking-wider">Participants</h2>
            
            {/* Client */}
            <div className="flex items-center gap-3 p-2 rounded-lg bg-red-500/10">
              <Avatar className="w-8 h-8">
                <AvatarImage src={bounty.client_avatar} />
                <AvatarFallback className="bg-red-900 text-red-200 text-xs">{bounty.client_username?.[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="text-red-100 text-sm font-medium cursor-pointer hover:text-red-50 hover:underline" onClick={() => setProfileUserId(bounty.client_id)}>{bounty.client_username}</p>
                <p className="text-xs text-red-400/60">Client</p>
              </div>
              <div className="flex items-center gap-1">
                <Star className="w-3 h-3 text-yellow-400" />
                <span className="text-xs text-yellow-300">{bounty.client_rep || 0}</span>
              </div>
              {bounty.client_confirmed && <Check className="w-4 h-4 text-green-400" />}
            </div>

            {/* Hunter */}
            {bounty.hunter_username ? (
              <div className="flex items-center gap-3 p-2 rounded-lg bg-blue-500/10">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={bounty.hunter_avatar} />
                  <AvatarFallback className="bg-blue-900 text-blue-200 text-xs">{bounty.hunter_username?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="text-blue-100 text-sm font-medium cursor-pointer hover:text-blue-50 hover:underline" onClick={() => setProfileUserId(bounty.hunter_id)}>{bounty.hunter_username}</p>
                  <p className="text-xs text-blue-400/60">Hunter</p>
                </div>
                <div className="flex items-center gap-1">
                  <Star className="w-3 h-3 text-yellow-400" />
                  <span className="text-xs text-yellow-300">{bounty.hunter_rep || 0}</span>
                </div>
                {bounty.hunter_confirmed && <Check className="w-4 h-4 text-green-400" />}
              </div>
            ) : (
              <div className="flex items-center gap-2 p-2 rounded-lg border border-dashed border-red-800/40 text-red-400/50 text-sm">
                <AlertCircle className="w-4 h-4" />
                Awaiting hunter...
              </div>
            )}

            {/* Contact IGNs */}
            {bounty.contact_igns && bounty.contact_igns.length > 0 && (
              <div className="pt-2 border-t border-red-900/30">
                <p className="text-xs text-red-400/60 mb-1">Contact IGNs</p>
                <div className="flex flex-wrap gap-1">
                  {bounty.contact_igns.map((ign, i) => (
                    <Badge key={i} className="bg-red-500/20 text-red-200 text-xs border-red-500/30">{ign}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Accept Bounty (for hunters when bounty is ACTIVE) */}
          {user && bounty.status === "ACTIVE" && bounty.client_id !== user.id && !bounty.hunter_id && (
            <div className="rounded-xl p-4 space-y-3" style={{ background: "linear-gradient(180deg, #0a1a08 0%, #041204 100%)", border: "1px solid rgba(34,197,94,0.3)" }}>
              <h2 className="text-sm font-semibold text-green-300 uppercase tracking-wider flex items-center gap-2">
                <Sword className="w-4 h-4" /> Accept This Bounty
              </h2>
              <p className="text-xs text-green-400/70">
                Accept this bounty to hunt {bounty.boss_name} for {formatMesos(bounty.reward)}. 
                Contact the buyer using the IGNs above once accepted.
              </p>
              <Button
                className="w-full text-sm"
                style={{ background: "linear-gradient(180deg, #16a34a 0%, #15803d 100%)", border: "1px solid #22c55e" }}
                onClick={handleAcceptBounty}
                disabled={confirming}
                data-testid="bounty-accept-btn"
              >
                {confirming ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Accept Bounty
              </Button>
            </div>
          )}

          {/* Action Buttons */}
          {bounty.is_participant && user && (bounty.status === "ASSIGNED" || bounty.status === "ACTIVE") && (
            <div className="rounded-xl p-4 space-y-3" style={{ background: "linear-gradient(180deg, #1a0808 0%, #120404 100%)", border: "1px solid rgba(220,38,38,0.25)" }}>
              <h2 className="text-sm font-semibold text-red-300 uppercase tracking-wider">Actions</h2>
              
              {/* Confirm Completion */}
              {bounty.status === "ASSIGNED" && (
                <div className="space-y-2">
                  {bounty.client_id === user.id && !bounty.client_confirmed && (
                    <Button
                      className="w-full text-sm"
                      style={{ background: "linear-gradient(180deg, #16a34a 0%, #15803d 100%)", border: "1px solid #22c55e" }}
                      onClick={handleConfirm}
                      disabled={confirming}
                      data-testid="bounty-confirm-btn"
                    >
                      {confirming ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                      Confirm Completion (Buyer)
                    </Button>
                  )}
                  {bounty.hunter_id === user.id && !bounty.hunter_confirmed && (
                    <Button
                      className="w-full text-sm"
                      style={{ background: "linear-gradient(180deg, #16a34a 0%, #15803d 100%)", border: "1px solid #22c55e" }}
                      onClick={handleConfirm}
                      disabled={confirming}
                      data-testid="bounty-confirm-btn"
                    >
                      {confirming ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                      Confirm Completion (Hunter)
                    </Button>
                  )}
                  {((bounty.client_id === user.id && bounty.client_confirmed) || 
                    (bounty.hunter_id === user.id && bounty.hunter_confirmed)) && (
                    <div className="flex items-center gap-2 text-green-400 text-sm p-2 rounded bg-green-500/10 border border-green-500/20">
                      <Check className="w-4 h-4" />
                      You confirmed. Waiting for other party...
                    </div>
                  )}
                </div>
              )}
              
              {/* Cancel */}
              <Button
                variant="destructive"
                className="w-full text-sm"
                onClick={handleCancel}
                disabled={confirming}
                data-testid="bounty-cancel-btn"
              >
                <X className="w-4 h-4 mr-2" />
                {bounty.client_id === user.id ? "Cancel Bounty" : "Drop Bounty"}
              </Button>
            </div>
          )}

          {/* Completed status */}
          {bounty.status === "SUCCEEDED" && (
            <div className="rounded-xl p-4 text-center" style={{ background: "linear-gradient(180deg, #1a1a08 0%, #121204 100%)", border: "1px solid rgba(234,179,8,0.3)" }}>
              <Check className="w-8 h-8 mx-auto text-yellow-400 mb-2" />
              <p className="text-yellow-300 font-semibold">Bounty Completed!</p>
              <p className="text-xs text-yellow-400/60">Reputation awarded to both parties</p>
            </div>
          )}

          {/* Delete button for INACTIVE/CANCELLED (creator only) */}
          {user && bounty.client_id === user.id && (bounty.status === "INACTIVE" || bounty.status === "CANCELLED") && (
            <Button
              variant="destructive"
              className="w-full text-sm"
              onClick={handleDelete}
              disabled={confirming}
              data-testid="bounty-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Bounty
            </Button>
          )}
        </div>

        {/* Right: Chat */}
        <div className="lg:col-span-2">
          {/* Disclaimer */}
          <div className="mb-2 px-3 py-2 rounded-lg flex items-start gap-2" style={{ background: "rgba(234,179,8,0.1)", border: "1px solid rgba(234,179,8,0.3)" }}>
            <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
            <p className="text-[11px] text-yellow-300/80">All trade agreements must be finalized and confirmed in-game. Hunter's Chance is not responsible for any disputes.</p>
            <div className="flex gap-1 ml-auto flex-shrink-0">
              <Button size="sm" variant="ghost" className="text-[#FF5E5B] hover:bg-[#FF5E5B]/10 h-6 px-2" onClick={() => setShowKofiModal(true)}>
                <Heart className="w-3 h-3 mr-1 fill-[#FF5E5B]" /> Support
              </Button>
              {user && (
                <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-6 px-2" onClick={() => setShowReportModal(true)} data-testid="report-bounty-btn">
                  <Flag className="w-3 h-3 mr-1" /> Report
                </Button>
              )}
            </div>
          </div>
          <div className="rounded-xl overflow-hidden flex flex-col" style={{ background: "linear-gradient(180deg, #1a0808 0%, #0f0303 100%)", border: "1px solid rgba(220,38,38,0.25)", height: "calc(100vh - 220px)" }}>
            {/* Chat Header */}
            <div className="px-4 py-3 border-b border-red-900/30 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-red-400" />
              <span className="text-red-200 text-sm font-semibold">Bounty Chat</span>
              <span className="text-xs text-red-400/40 ml-auto">{messages.length} messages</span>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 px-4 py-3">
              <div className="space-y-3">
                {messages.length === 0 && (
                  <div className="text-center py-12 text-red-400/40">
                    <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No messages yet. Start the conversation!</p>
                  </div>
                )}
                {messages.map((msg) => {
                  const isMe = user && msg.user_id === user.id;
                  return (
                    <div key={msg.id} className={`flex gap-2 ${isMe ? "flex-row-reverse" : ""}`}>
                      <Avatar className="w-7 h-7 flex-shrink-0">
                        <AvatarImage src={msg.avatar} />
                        <AvatarFallback className="bg-red-900/50 text-red-200 text-xs">{msg.username?.[0]?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div className={`max-w-[70%] ${isMe ? "items-end" : "items-start"}`}>
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className={`text-xs font-medium cursor-pointer hover:underline ${isMe ? "text-red-300" : "text-red-400/70"}`} onClick={() => setProfileUserId(msg.user_id)}>{msg.username}</span>
                          <span className="text-xs text-red-400/30">{formatTime(msg.created_at)}</span>
                        </div>
                        <div className={`rounded-xl px-3 py-2 text-sm ${isMe ? "bg-red-600/30 text-red-100" : "bg-black/40 text-red-200"}`}>
                          {msg.message}
                          {msg.screenshot_url && (
                            <img src={msg.screenshot_url.startsWith("http") ? msg.screenshot_url : `${API_URL}${msg.screenshot_url}`} alt="Screenshot" className="mt-2 rounded-lg max-w-full max-h-48 cursor-pointer" onClick={() => window.open(msg.screenshot_url.startsWith("http") ? msg.screenshot_url : `${API_URL}${msg.screenshot_url}`, "_blank")} />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={chatEndRef} />
              </div>
            </ScrollArea>

            {/* Chat Input */}
            {bounty.is_participant && (
              <div className="px-4 py-3 border-t border-red-900/30">
                <div className="flex items-center gap-2">
                  <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleUploadScreenshot} />
                  <Button variant="ghost" size="icon" className="text-red-400 hover:bg-red-500/10 h-9 w-9" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="bounty-chat-upload">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Image className="w-4 h-4" />}
                  </Button>
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSendMessage()}
                    onPaste={async (e) => {
                      const items = e.clipboardData?.items;
                      if (!items) return;
                      for (const item of items) {
                        if (item.type.startsWith('image/')) {
                          e.preventDefault();
                          const file = item.getAsFile();
                          if (file) {
                            const fakeEvent = { target: { files: [file] } };
                            handleUploadScreenshot(fakeEvent);
                          }
                          break;
                        }
                      }
                    }}
                    placeholder="Type a message... (Ctrl+V to paste image)"
                    className="bg-black/40 border-red-900/40 text-red-100 placeholder:text-red-400/30 h-9"
                    data-testid="bounty-chat-input"
                  />
                  <Button size="icon" className="h-9 w-9" style={{ background: "linear-gradient(180deg, #dc2626 0%, #991b1b 100%)" }} onClick={() => handleSendMessage()} disabled={sendingMessage || !newMessage.trim()} data-testid="bounty-chat-send">
                    {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            )}

            {!bounty.is_participant && (
              <div className="px-4 py-3 border-t border-red-900/30 text-center text-red-400/40 text-sm">
                Only participants can chat
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Report Modal */}
      <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400"><Flag className="w-4 h-4" /> Report Bounty</DialogTitle>
          </DialogHeader>
          <Textarea
            placeholder="Describe the issue..."
            value={reportReason}
            onChange={(e) => setReportReason(e.target.value)}
            className="min-h-[100px]"
            data-testid="report-reason-input"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowReportModal(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={handleReport} disabled={reporting || !reportReason.trim()} data-testid="report-submit-btn">
              {reporting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Flag className="w-4 h-4 mr-1" />} Submit Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <KofiModal open={showKofiModal} onOpenChange={setShowKofiModal} />
      <UserProfileModal
        open={!!profileUserId}
        onOpenChange={(v) => { if (!v) setProfileUserId(null); }}
        userId={profileUserId}
      />
    </div>
  );
};

export default BountyDetailPage;